import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { credentialLabel } from "@/lib/labels";
import { Award, ShieldCheck, FileDown, EyeOff, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { AdminActionsMenu } from "@/components/admin/AdminActionsMenu";
import { EditRecordDrawer } from "@/components/admin/EditRecordDrawer";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const CRED_FILTERS = [
  { key: "search", type: "search", label: "Search holder name or credential number", paths: ["holder_name", "credential_number"] },
  { key: "credential_type", type: "select", label: "Credential type", path: "credential_type",
    options: [
      { value: "BCT", label: "BCT" }, { value: "CHTC", label: "CHTC" },
      { value: "CTS", label: "CTS" }, { value: "LHT", label: "LHT" },
    ] },
  { key: "status", type: "select", label: "Status", path: "status",
    options: [
      { value: "active", label: "Active" }, { value: "suspended", label: "Suspended" },
      { value: "revoked", label: "Revoked" }, { value: "expired", label: "Expired" },
    ] },
  { key: "organization", type: "select", label: "Board", path: "organization",
    options: [{ value: "IBTHM", label: "IBTHM" }, { value: "ACIS", label: "ACIS" }] },
  { key: "state", type: "text", label: "State", path: "state", placeholder: "e.g. CA" },
  { key: "date_from", type: "dateFrom", label: "Issued from", path: "issue_date" },
  { key: "date_to", type: "dateTo", label: "Issued to", path: "issue_date" },
  { key: "visible_in_registry", type: "boolean", label: "Visible in registry", path: "visible_in_registry" },
  { key: "is_archived", type: "boolean", label: "Archived", path: "is_archived" },
  { key: "is_sample", type: "boolean", label: "Sample", path: "is_sample" },
  { key: "is_deleted", type: "boolean", label: "Deleted", path: "is_deleted" },
];

export default function Credentials() {
  const { user } = useAuth();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/credentials");
      setCreds(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const filters = useFilters(creds, CRED_FILTERS, {
    defaultSort: { field: "issue_date", dir: "desc" },
  });
  const selection = useSelection(filters.filtered);

  const isAdmin = user.role === "admin";

  return (
    <div className="animate-fade-up" data-testid="credentials-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Credentials</div>
        <h1 className="font-heading text-3xl font-bold text-primary">{isAdmin || user.role === "reviewer" ? "All credentials" : "My credentials"}</h1>
        <p className="text-sm text-slate-500 mt-1">Issued credentials are listed in the public registry.</p>
      </div>

      <FilterPanel
        schemas={CRED_FILTERS}
        values={filters.values}
        set={filters.set}
        clear={filters.clear}
        count={filters.count}
        total={creds.length}
        activeCount={filters.activeCount}
        sortFields={[
          { key: "issue_date", label: "Issue date" },
          { key: "expiry_date", label: "Expiration date" },
          { key: "holder_name", label: "Practitioner name" },
          { key: "credential_number", label: "Credential number" },
          { key: "status", label: "Status" },
        ]}
        sort={filters.sort}
        setSort={filters.setSort}
        testid="creds-filters"
      />

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && filters.filtered.length === 0 && (
        <Card className="p-10 text-center border-dashed">
          <Award className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading font-semibold text-primary">No credentials</div>
          <div className="text-sm text-slate-500 mt-1">Try clearing the filters or check back when new credentials are issued.</div>
        </Card>
      )}

      {isAdmin && (
        <div className="flex items-center gap-3 mb-3">
          <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="creds-select-all" />
          <span className="text-xs text-slate-600">Select all visible</span>
        </div>
      )}
      {isAdmin && <BulkActionsBar collection="credentials" selection={selection} onDone={reload} />}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filters.filtered.map((c) => (
          <Card key={c.id} className={`p-5 border-slate-200 shadow-sm relative ${selection.isSelected(c.id) ? "ring-2 ring-primary" : ""}`} data-testid={`credential-card-${c.credential_number}`}>
            {isAdmin && (
              <div className="absolute top-3 left-3">
                <Checkbox checked={selection.isSelected(c.id)} onCheckedChange={() => selection.toggle(c.id)} data-testid={`cred-select-${c.id}`} />
              </div>
            )}
            <div className={`flex items-start justify-between mb-3 ${isAdmin ? "pl-7" : ""}`}>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-primary flex items-center gap-2 flex-wrap">
                  {c.holder_name}
                  {c.is_sample && (
                    <span className="text-[9px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <FlaskConical className="h-2.5 w-2.5" /> Sample
                    </span>
                  )}
                  {c.visible_in_registry === false && (
                    <span className="text-[9px] uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <EyeOff className="h-2.5 w-2.5" /> Hidden
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500" title={c.credential_type}>{credentialLabel(c.credential_type)}{c.state ? ` · ${c.state}` : ""}</div>
              </div>
              <div className="flex items-center gap-1">
                <StatusBadge status={c.status} />
                {isAdmin && (
                  <AdminActionsMenu
                    collection="credentials"
                    record={c}
                    onEdit={(r) => setEditing(r)}
                    onChange={reload}
                  />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
              <Cell label="Number" value={c.credential_number} mono />
              <Cell label="Board" value={c.organization} />
              <Cell label="Issued" value={new Date(c.issue_date).toLocaleDateString()} />
              <Cell label="Expires" value={new Date(c.expiry_date).toLocaleDateString()} />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Link to={`/verify/${c.credential_number}`} className="text-xs text-primary hover:underline">Public verify →</Link>
              {(isAdmin || c.holder_user_id === user.id) && c.status === "active" && (
                <Link
                  to={`/certificate/credential/${c.credential_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  data-testid={`cred-download-${c.credential_number}`}
                >
                  <FileDown className="h-3.5 w-3.5" /> Download certificate
                </Link>
              )}
            </div>
          </Card>
        ))}
      </div>
      <EditRecordDrawer
        collection="credentials"
        record={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={reload}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    suspended: "bg-amber-50 text-amber-800 border-amber-200",
    revoked: "bg-red-50 text-red-800 border-red-200",
    expired: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <Badge variant="outline" className={`capitalize ${map[status]}`}>
      {status === "active" && <ShieldCheck className="h-3 w-3 mr-1" />}
      {status}
    </Badge>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono-data text-primary font-semibold" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}
